import './BarCodePrint.scss';

import cn from 'classnames';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Barcode from 'react-barcode';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { MARGIN_KEY_PARAM } from '@/common/constants';
import print from '@/common/helpers/print';
import useQueryParams from '@/common/hooks/useQueryParams';
import Image from '@/ebat/components/blocks/Standalone/BarCodePrint/Picture/Picture';
import {
  PRODUCT_BARCODE_MARGINS,
  PRODUCT_BARCODE_PARAMS,
  PRODUCT_BARCODE_SIZES,
} from '@/ebat/constants';
import useWishlist from '@/ebat/hooks/wishlists/useWishlist';

const BarCodePrint = () => {
  const { t } = useTranslation();
  const { id, size } = useParams();
  const getQueryParams = useQueryParams();

  const imagesCountRef = useRef(0);

  const [isLoaded, setIsLoaded] = useState(false);

  const {
    data: wishlist,
    isLoading: isLoadingWishlist,
    isSuccess,
    isError,
  } = useWishlist(id);

  const products = useMemo(
    () =>
      wishlist?.items.filter(
        (item) =>
          (item.is_selected &&
            item.product?.is_salable &&
            !item.product?.must_substitute) ||
          (item.is_selected && !item?.product),
      ),
    [wishlist],
  );

  const isBig = useMemo(() => size === PRODUCT_BARCODE_SIZES.BIG, [size]);

  /**
   * Check loading or error event on image to print asynchronously the page with best render of barcodes. Only for big render.
   *
   * @type {function(): void}
   */
  const onLoadImage = useCallback(() => {
    imagesCountRef.current += 1;

    if (
      imagesCountRef.current >=
      products.filter((item) => item.product?.image).length
    ) {
      setIsLoaded(true);
    }
  }, [products]);

  /**
   * Print page for:
   * - small view
   * - all views if errors occur
   */
  useEffect(() => {
    if (
      (isSuccess && !isLoadingWishlist && (!isBig || isLoaded)) ||
      (isError && !isLoadingWishlist)
    ) {
      print();
    }
  }, [isBig, isError, isLoaded, isLoadingWishlist, isSuccess]);

  useEffect(() => {
    const margin = getQueryParams.get(MARGIN_KEY_PARAM);
    const style = document.createElement('style');

    if (PRODUCT_BARCODE_MARGINS[margin]) {
      style.innerHTML = `@page { margin: ${PRODUCT_BARCODE_MARGINS[margin]} }`;
      document.head.appendChild(style);
      document.body.classList.remove(...Object.keys(PRODUCT_BARCODE_MARGINS));
      document.body.classList.add(margin);
    }
  }, [getQueryParams]);

  if (isError) {
    return (
      <main className={cn('barcodes-print', `barcodes-print--view-${size}`)}>
        <div className="barcodes-print__error">
          {t('common:messages:forms.errors.default')}
        </div>
      </main>
    );
  }

  if (isSuccess) {
    return (
      <main className={cn('barcodes-print', `barcodes-print--view-${size}`)}>
        <ul className="barcodes-print__list">
          {products.map((item) => (
            <li key={`barcode-print-${item.id}`} className="barcode">
              <span className="barcode__name">
                {item.product?.name || item.label}
              </span>
              <span className="barcode__brand">{item.product?.brand.name}</span>
              <div className="barcode__barcode">
                <Barcode
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...PRODUCT_BARCODE_PARAMS[size]}
                  value={item.product?.sku || item.sku}
                />
              </div>
              {isBig && (
                <div className="barcode__image">
                  <Image
                    alt={item.product?.image?.alt}
                    onLoadImage={onLoadImage}
                    src={item.product?.image?.url}
                  />
                </div>
              )}
            </li>
          ))}
        </ul>
      </main>
    );
  }

  return null;
};

export default BarCodePrint;
